import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Role names that receive full access bypass (case-insensitive match)
const FULL_ACCESS_ROLE_NAMES = ["admin", "super_admin", "controller"];

// Vendor / Personnel / Contract Labor / Customer role names for gating
const VENDOR_ROLE_NAME = "vendor";
const PERSONNEL_ROLE_NAME = "personnel";
const CONTRACT_LABOR_ROLE_NAME = "contract_labor";
const CUSTOMER_ROLE_NAMES = ["customer_admin", "customer_user"];

export const usePermissions = () => {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["user-permissions", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Get the user's active company (if set)
      const { data: activeRow } = await supabase
        .from("user_active_company")
        .select("company_id")
        .eq("user_id", user.id)
        .maybeSingle();

      let q = supabase
        .from("user_profiles")
        .select("*, role:roles(*)")
        .eq("user_id", user.id);

      if (activeRow?.company_id) {
        q = q.eq("company_id", activeRow.company_id);
      }

      const { data, error } = await q.limit(1).maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const userProfile = query.data;
  const role = userProfile?.role as any;
  const roleId: string | undefined = userProfile?.role_id;
  const roleName: string | undefined = role?.name;
  const permissions: Record<string, boolean> = role?.permissions ?? {};

  // Derive admin status from role name instead of hardcoded UUIDs
  const isAdmin = !!roleName && FULL_ACCESS_ROLE_NAMES.includes(roleName.toLowerCase());
  const isVendor = !!roleName && roleName.toLowerCase() === VENDOR_ROLE_NAME;
  const isPersonnel = !!roleName && roleName.toLowerCase() === PERSONNEL_ROLE_NAME;
  const isContractLabor = !!roleName && roleName.toLowerCase() === CONTRACT_LABOR_ROLE_NAME;
  const isCustomer = !!roleName && CUSTOMER_ROLE_NAMES.includes(roleName.toLowerCase());
  const isCustomerAdmin = !!roleName && roleName.toLowerCase() === "customer_admin";

  const can = (permission: string): boolean => {
    // Return false while loading to prevent permission bypass on initial load
    if (query.isLoading || !userProfile) return false;
    if (isAdmin) return true;
    if (isVendor || isPersonnel || isContractLabor || isCustomer) return false;
    return permissions[permission] === true;
  };

  // Legacy compat: return data as string[] for RequirePermission
  const legacyPerms = Object.entries(permissions)
    .filter(([, v]) => v === true)
    .map(([k]) => k);
  if (isAdmin) {
    legacyPerms.push("*");
  }

  return {
    can,
    isAdmin,
    isVendor,
    isPersonnel,
    isContractLabor,
    isCustomer,
    isCustomerAdmin,
    roleName,
    roleId,
    role,
    userProfile,
    permissions,
    data: legacyPerms,
    isLoading: query.isLoading,
  };
};
